import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Block as BlockT } from '@shared/types';
import { Block } from '@/components/Block/Block';
import { MobileParamSheet, ParamPanel } from '@/components/Block/ParamPanel';
import { SfxTrack } from '@/components/Timeline/SfxTrack';
import { VideoPlayer } from '@/components/VideoPlayer/VideoPlayer';
import { Button } from '@/components/ui/Button';
import { UploadModal } from '@/components/UploadModal/UploadModal';
import {
  applyPredefinedFreeVoiceFallback,
  selectAllAssets,
  useSessionStore,
} from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { useToast } from '@/components/ToastProvider';
import { useCloudVideoSize } from '@/hooks/useCloudVideoSize';
import { fileLangCode } from '@/lib/uploadFilenames';
import { detectScript } from '@/lib/scriptDetect';
import { getPrimaryVoiceId, resolveBlockVoiceId } from '@/lib/voiceMigration';
import { resolveModelLock } from '@/lib/modelLock';
import { isFlexAsset } from '@/lib/assetUi';
import { withSearch } from '@/lib/nav';
import { ScrollHint } from '@/components/ScrollHint/ScrollHint';
import { CoachMark } from '@/components/CoachMark/CoachMark';
import { COACH_MARK_TEXTS } from '@/lib/coachMarks';
import { useCoachMarks } from './hooks/useCoachMarks';
import { BottomBar } from './BottomBar';
import { useAssetUploadSubmit } from './hooks/useAssetUploadSubmit';
import { useTakePlayback } from './hooks/useTakePlayback';
import { useTtsGeneration } from './hooks/useTtsGeneration';
import { useBlockEditing } from './hooks/useBlockEditing';
import styles from './TTSView.module.css';
import { ScriptHeader } from './components/ScriptHeader';
import { TtsTimelineBand } from './components/TtsTimelineBand';
import { makeEmptyBlock } from './blockFactory';
import { scrollPanelIntoView as scrollPanelIntoViewEl } from './scrollPanelIntoView';

/** `data-coach`-Ziele je Station der Hinweis-Tour (lib/coachMarks.ts). */
const COACH_TARGETS = {
  autosplit: ['autosplit'],
  generate: ['generate-all', 'generate-block'],
  timeline: ['timeline'],
  upload: ['upload'],
} as const;

const scriptHeaderClasses = {
  row: styles.scriptBoxLabel,
  actions: styles.scriptBoxActions,
  button: styles.scriptCopyBtn,
  copied: styles.scriptCopyBtnCopied,
};

export function TTSView() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);
  const updateAssetBlocks = useSessionStore((s) => s.updateAssetBlocks);
  const setCurrentAssetIdx = useSessionStore((s) => s.setCurrentAssetIdx);
  const toolId = useSessionStore((s) => s.toolId);
  // Read each slice separately so React re-renders only when its specific
  // dependency changes. `selectAllAssets` builds a fresh array each call, so
  // subscribing to it directly through useSessionStore would loop.
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const totalAssets = useSessionStore((s) => selectAllAssets(s).length);
  // Slices needed for upload readiness, downloads and the post-upload reset.
  // Subscribed individually so unrelated store updates don't re-render; the
  // upload-only slices live in useAssetUploadSubmit.
  const versions = useSessionStore((s) => s.versions);
  const sessionFolder = useSessionStore((s) => s.sessionFolder);
  const shortId = useSessionStore((s) => s.shortId);
  const config = useSessionStore((s) => s.config);
  const freeMode = useSessionStore((s) => s.freeMode);
  const setFreeBlocks = useSessionStore((s) => s.setFreeBlocks);
  const setFreeActiveIdx = useSessionStore((s) => s.setFreeActiveIdx);
  const sessionUploads = useSessionStore((s) => s.sessionUploads);
  const videoBlobs = useVideoStore((s) => s.blobs);
  // Mirror selectAllAssets but inline so we can subscribe to slices
  // individually (the live selector rebuilds its array each call). Includes
  // vanilla's predefined free-choice voice fallback (legacy line 4014).
  const configVoices = useSessionStore((s) => s.config?.voices);
  const activeAsset = useMemo(() => {
    const cfg = applyPredefinedFreeVoiceFallback(configAssets, configVoices);
    // `_isCustom` MUSS hier gesetzt werden: der Selector `selectAllAssets`
    // taggt Custom-Assets, diese View merged die Liste aber selbst — ohne Tag
    // war `activeAsset._isCustom` immer undefined. Folge: der Upload legte
    // `1 - Titel` statt `1 - Custom - Titel` an, während der Löschpfad
    // (customAssetOps) den Custom-Namen suchte → WebDAV-404, das als Erfolg
    // gewertet wurde: Asset lokal weg, Cloud-Ordner blieb liegen.
    // Siehe docs/decisions.md 2026-08-13.
    const merged = [...cfg, ...customAssets.map((ca) => ({ ...ca, _isCustom: true as const }))];
    return merged[currentAssetIdx];
  }, [configAssets, configVoices, customAssets, currentAssetIdx]);
  // Multi-voice (#173) Phase 1: until the per-block picker lands in Phase 5,
  // every newly-constructed block defaults to the first allowed voice.
  const activeAssetVoiceId = getPrimaryVoiceId(activeAsset);
  const toast = useToast();
  // Multi-Lingual: `simpleMode` heißt seit Spec 2026-09-21 § 6 nur noch
  // „Multi-Lingual-Talent" — kein SfxTrack, keine Custom-Assets.
  // `config` is already subscribed above; derive rather than adding a
  // second store subscription (keeps hook order untouched).
  const simpleMode = config?._simpleMode === true;
  // Video, Mixer, Overflow-Schraffur und Zeitlimit-Pill entscheiden sich
  // dagegen PRO ASSET: nur ein Asset ohne Video bleibt flexibel. Für die
  // bestehenden Multi-Projekte (nur TTS, kein Video) ist das für jedes
  // Asset `true` — also unverändert das bisherige Verhalten.
  const flexAsset = isFlexAsset(config, activeAsset);

  // Custom assets have their own per-mode editable scripts (`scriptTts` /
  // `scriptSts`) so switching between TTS and STS doesn't clobber work in
  // the other mode. Falls back to legacy `script` for assets created before
  // those fields existed (silent migration). Predefined assets keep using
  // the admin-set `script`.
  const seedScript = activeAsset?._isCustom
    ? activeAsset?.scriptTts ?? activeAsset?.script ?? ''
    : activeAsset?.script ?? '';

  const blocks = useMemo<BlockT[]>(() => {
    const existing = assetBlocks[currentAssetIdx];
    if (existing && existing.length > 0) return existing;
    // Seed from the asset's predefined script (vanilla `initBlocks(script)`
    // pattern) so the talent isn't staring at an empty editor for tools that
    // ship a script. Custom assets fall through with an empty block.
    return [makeEmptyBlock(seedScript, activeAssetVoiceId)];
  }, [assetBlocks, currentAssetIdx, seedScript, activeAssetVoiceId]);

  // Ensure the store has at least one block for this asset so downstream
  // reads (param updates, generation in later tasks) see the same shape.
  // Also re-seed if the only existing block is the unedited default
  // (empty text, no takes, unlocked) — that covers projects opened
  // before the seed-from-script fix landed and persisted an empty block.
  // Der Legacy-Re-Seed läuft nur beim ERSTEN Antreffen eines Assets pro
  // Mount: leert das Talent den einzigen Block selbst, darf nicht sofort
  // das Originalskript zurückspringen (Audit, Low).
  const seedCheckedRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const existing = assetBlocks[currentAssetIdx];
    if (!existing || existing.length === 0) {
      seedCheckedRef.current.add(currentAssetIdx);
      updateActiveBlocks(() => [makeEmptyBlock(seedScript, activeAssetVoiceId)]);
      return;
    }
    if (seedCheckedRef.current.has(currentAssetIdx)) return;
    seedCheckedRef.current.add(currentAssetIdx);
    const onlyEmptyDefault =
      existing.length === 1 &&
      !existing[0].text.trim() &&
      existing[0].takes.length === 0 &&
      !existing[0].locked;
    if (onlyEmptyDefault && seedScript) {
      updateActiveBlocks(() => [makeEmptyBlock(seedScript, activeAssetVoiceId)]);
    }
  }, [currentAssetIdx, assetBlocks, updateActiveBlocks, seedScript, activeAssetVoiceId]);

  const [activeBlockIdx, setActiveBlockIdx] = useState(0);

  // Model-Lock (spec 2026-07-23): clamp every block's params.model to the
  // effective lock of its voice. One central effect covers freshly seeded
  // blocks (default 'v2'), per-block voice switches, and blocks persisted
  // before the admin added a restriction. No-op when nothing is locked.
  useEffect(() => {
    const assetVoiceIds = activeAsset?.voiceIds ?? [];
    const voices = configVoices ?? [];
    const lockFor = (b: BlockT) =>
      resolveModelLock(activeAsset, resolveBlockVoiceId(b, assetVoiceIds), voices);
    if (!blocks.some((b) => {
      const lock = lockFor(b);
      return lock !== null && b.params.model !== lock;
    })) {
      return;
    }
    updateActiveBlocks((prev) =>
      prev.map((b) => {
        const lock = lockFor(b);
        return lock && b.params.model !== lock
          ? { ...b, params: { ...b.params, model: lock } }
          : b;
      }),
    );
  }, [blocks, activeAsset, configVoices, updateActiveBlocks]);

  // Indices of blocks whose slide-in animation should play. A block is "new"
  // only on the render after it appears; then it's removed from the set so
  // subsequent re-renders don't replay the keyframes.
  const [newBlockIdx, setNewBlockIdx] = useState<number | null>(null);

  // Clamp activeBlockIdx if blocks shrank.
  useEffect(() => {
    if (activeBlockIdx >= blocks.length) {
      setActiveBlockIdx(Math.max(0, blocks.length - 1));
    }
  }, [activeBlockIdx, blocks.length]);

  // Refs into block DOM for panel positioning.
  const blockRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const blocksColRef = useRef<HTMLDivElement | null>(null);
  // Sticky timeline strip — used by the click-driven scroll-into-view
  // check so the ParamPanel's top can never sit clipped behind it.
  const timelineStickyRef = useRef<HTMLDivElement | null>(null);
  // Outer .col wrapper of ParamPanel — used to read its rect after the
  // panel re-positions on user-click activation.
  const paramPanelRef = useRef<HTMLDivElement | null>(null);

  // The active block's DOM element flows through state, not a render-time read
  // of `blockRefs.current`. After Auto-Split (or any all-blocks-replaced path),
  // the parent renders BEFORE the new block mounts, so a render-time read
  // returns the stale (or null) element and ParamPanel positions against the
  // wrong offsetTop. Routing via state guarantees the panel re-runs its
  // layout effect with the freshly-mounted element.
  const [activeBlockEl, setActiveBlockEl] = useState<HTMLElement | null>(null);
  const activeBlockIdxRef = useRef(0);
  activeBlockIdxRef.current = activeBlockIdx;
  const registerRef = useCallback((idx: number, el: HTMLDivElement | null) => {
    if (el) blockRefs.current[idx] = el;
    else delete blockRefs.current[idx];
    if (idx === activeBlockIdxRef.current) setActiveBlockEl(el);
  }, []);
  // When the active index changes (without a remount), pull the fresh element
  // out of the ref map so the panel can reposition.
  useLayoutEffect(() => {
    setActiveBlockEl(blockRefs.current[activeBlockIdx] ?? null);
  }, [activeBlockIdx]);

  // Bump this token after structural changes so ParamPanel skips the
  // margin-top transition for one paint (matches legacy _panelSkipTransition).
  const [skipToken, setSkipToken] = useState(0);
  const bumpSkip = useCallback(() => setSkipToken((t) => t + 1), []);

  // Mobile (<600px) ParamPanel bottom-sheet. The desktop column hides via CSS
  // at narrow viewports; opening is driven by the per-block "Edit Params"
  // CTA in Block. Selecting a block also activates it so the sheet shows
  // the same data the desktop arrow would have pointed at.
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const handleOpenParams = useCallback((idx: number) => {
    setActiveBlockIdx(idx);
    setMobilePanelOpen(true);
  }, []);
  const closeMobilePanel = useCallback(() => setMobilePanelOpen(false), []);

  const activeBlock = blocks[activeBlockIdx] ?? blocks[0];

  // Clear new-block flag after one render so the animation doesn't replay.
  useEffect(() => {
    if (newBlockIdx == null) return;
    const t = window.setTimeout(() => setNewBlockIdx(null), 250);
    return () => window.clearTimeout(t);
  }, [newBlockIdx]);

  // Scroll the content pane so the whole ParamPanel is visible after a
  // user-click activation (see ./scrollPanelIntoView.ts for the geometry).
  const scrollPanelIntoView = useCallback((idx: number) => {
    scrollPanelIntoViewEl(
      blockRefs.current[idx],
      paramPanelRef.current,
      timelineStickyRef.current,
    );
  }, []);

  const handleActivate = useCallback((idx: number) => {
    setActiveBlockIdx(idx);
    // Two RAFs: first lets ParamPanel re-run its layout effect (margin-top
    // shift), second measures the post-shift rect.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollPanelIntoView(idx));
    });
  }, [scrollPanelIntoView]);

  // Upload-modal visibility. Lives in TTSView so the close handler can
  // strip takes after a successful upload (vanilla parity, legacy
  // 10003-10018) — see handleUploadSubmit below.
  const [uploadOpen, setUploadOpen] = useState(false);


  // ── Generation + playback ────────────────────────────────────────────
  //
  // Take playback (chips, Play-All-Favorites) and the pipeline state shared
  // with Generate-All live in useTakePlayback; single-block Generate,
  // Generate-All and the cancel-on-asset-switch effect in useTtsGeneration.
  const playback = useTakePlayback({ blocks, currentAssetIdx, toast });
  const {
    playing,
    pipelineMode,
    pipelineBlockIdx,
    pipelineGenIdx,
    handlePlayAllFavorites,
    handleChipPlay,
  } = playback;
  const {
    generatingIdx,
    handleGenerate,
    handleGenerateAll,
  } = useTtsGeneration({
    blocks,
    activeAsset,
    activeAssetVoiceId,
    currentAssetIdx,
    toolId,
    toast,
    updateAssetBlocks,
    playback,
  });


  const disabledReason = (() => {
    if (!toolId) return 'Loading…';
    if (!activeAsset) return 'No asset selected';
    if (!activeAssetVoiceId) return 'Select a voice first';
    return null;
  })();

  // ── Upload pipeline plumbing ─────────────────────────────────────────
  // Mirrors vanilla updateUploadVersionBtn (legacy 10080-10090): EVERY
  // block must have a fav take with content before upload is allowed.
  // `readyBlocks` (filter) is kept for the modal's count display only —
  // the gate itself uses `allBlocksReady` (every) so a single ready
  // block can't unlock the upload while others are still empty.
  const allBlocksReady = useMemo(
    () =>
      blocks.length > 0 &&
      blocks.every(
        (b) => b.favIdx >= 0 && !!b.takes[b.favIdx] && !!b.text?.trim(),
      ),
    [blocks],
  );
  // Sprechender Grund für den disabled Upload-Button. StatusDots zeigen
  // nur fav+take, der Upload-Gate verlangt zusätzlich nicht-leeren Text —
  // also "alle Punkte grün" + dennoch grauer Button ist eine echte UX-Falle.
  // Tooltip benennt den exakten Block.
  const uploadDisabledReason = useMemo<string | null>(() => {
    if (!sessionFolder) return 'Session is still initialising';
    if (blocks.length === 0) return 'Add at least one block';
    const noText = blocks.findIndex((b) => !b.text?.trim());
    if (noText >= 0) return `Block ${noText + 1} has no text`;
    const noFav = blocks.findIndex(
      (b) => b.favIdx < 0 || !b.takes[b.favIdx],
    );
    if (noFav >= 0) return `Block ${noFav + 1} has no favorite take`;
    return null;
  }, [blocks, sessionFolder]);
  const readyBlocks = useMemo(
    () =>
      blocks.filter(
        (b) => b.favIdx >= 0 && !!b.takes[b.favIdx] && !!b.text?.trim(),
      ),
    [blocks],
  );
  const hasVideoForActive = !!videoBlobs[currentAssetIdx];
  // Cloud-Video des Admins — nur wenn das Talent kein eigenes Video geladen
  // hat, ist es die Quelle für das Premix-MP4 (Spec 2026-09-21 § 7). Die
  // Größe entscheidet, ob es automatisch mitgeht oder das Modal fragt.
  const cloudVideoPath =
    !hasVideoForActive && activeAsset?.videoPath ? activeAsset.videoPath : null;
  const cloudVideoSize = useCloudVideoSize(toolId, cloudVideoPath);
  // langCode prefix: prefer the explicit admin field; fall back to the
  // first 3 chars of the project name. `'TRO'` is a defensive last resort
  // so filenames never end up starting with `_`.
  const langCode = fileLangCode(config);
  const voiceName =
    config?.voices?.find((v) => v.id === activeAssetVoiceId)?.name ?? 'Voice';
  // selectAllAssets tags custom assets with `_isCustom: true`. Use that
  // flag rather than re-deriving from `currentAssetIdx >= configAssets.length`
  // so reordering can't desync the two views.
  const isCustom = !!activeAsset?._isCustom;
  const nextVersion = useMemo(() => {
    const list = versions[activeAsset?.id ?? ''] ?? [];
    return list.length > 0 ? Math.max(...list.map((v) => v.version)) + 1 : 1;
  }, [versions, activeAsset?.id]);

  // Post-upload reset (vanilla parity, legacy 10003-10018): strip takes from
  // the active asset's blocks so the user starts the next iteration with
  // empty chips and can't accidentally re-upload identical takes. (Takes
  // carry no blob: URL any more — nothing to revoke; the decode cache is
  // bounded, the IDB audio entries are cleaned up on the next autosave.)
  const handleUploaded = useCallback(() => {
    if (!activeAsset) return;
    updateActiveBlocks((prev) => {
      return prev.map((b) => ({
        ...b,
        takes: [],
        favIdx: -1,
        takeCounter: 0,
        offset: 0,
        pinned: false,
        // Locks are an in-iteration safeguard ("don't accidentally edit
        // this block while I work on the next one"). Once the version
        // ships, that intent is satisfied — drop the lock so the next
        // iteration starts fresh and the talent doesn't have to manually
        // unlock every block they want to revise.
        locked: false,
      }));
    });
    setActiveBlockIdx(0);
    // For free / custom assets that route through FreeChoiceView, also
    // reset the per-mode freeBlocks slot + the active-block index so the
    // next mount of FreeChoiceView (e.g. after toggling tts/sts) starts
    // fresh in the active mode. Mirrors vanilla submitVersionUpload
    // cleanup (legacy 10018-10024).
    if (activeAsset.assetType === 'free' || isCustom) {
      const mode = (freeMode[currentAssetIdx] ?? 'tts') as 'tts' | 'sts';
      if (mode === 'tts') {
        setFreeBlocks(currentAssetIdx, 'tts', []);
        setFreeActiveIdx(currentAssetIdx, 0);
      }
    }
  }, [activeAsset, isCustom, freeMode, currentAssetIdx, updateActiveBlocks, setFreeBlocks, setFreeActiveIdx]);

  // Upload-modal submit (lib: useAssetUploadSubmit, shared with STSView).
  const handleUploadSubmit = useAssetUploadSubmit({
    activeAsset,
    currentAssetIdx,
    isCustom,
    customScriptField: 'scriptTts',
    voiceName,
    blocks,
    isSts: activeAsset?.assetType === 'sts',
    nextVersion,
    cloudVideoSize,
    logTag: 'TTSView',
    onUploaded: handleUploaded,
  });

  // ── Block mutations, chips, params (useBlockEditing) ───────────────────
  const {
    handleTextChange,
    handleToggleLock,
    handleDuplicate,
    handleMergeUp,
    handleMergeDown,
    handleSplit,
    handleDelete,
    handleDownloadBlock,
    handleMoveUp,
    handleMoveDown,
    handleResetPositions,
    handleBlocksReordered,
    autoSplitFeedback,
    handleAutoSplitAll,
    handleRestoreScript,
    hintTakeId,
    dismissStarHint,
    handleChipFav,
    handleChipDelete,
    handleAddBlock,
    handleParamsChange,
    handleBlockVoiceChange,
  } = useBlockEditing({
    blocks,
    activeBlock,
    activeBlockIdx,
    setActiveBlockIdx,
    setNewBlockIdx,
    bumpSkip,
    activeAsset,
    activeAssetVoiceId,
    currentAssetIdx,
    langCode,
    shortId,
    nextVersion,
    toast,
    playback,
  });

  // Geführte Einmal-Hinweise (Auto-Split → Generate → Timeline → Upload);
  // der Stern-Hinweis am ersten Take bleibt in useBlockEditing.
  const coach = useCoachMarks({
    blocks,
    blocksColEl: blocksColRef.current,
    allReady: allBlocksReady,
    sessionUploads,
  });
  // Derived flags for the two pipeline buttons. A pipeline can't start while
  // a single-block Generate is in flight, while the OTHER pipeline is
  // running, or when there's no work to do. Clicking the button that's
  // currently running always cancels — so the disabled check is asymmetric.
  const hasUnlockedNonEmpty = blocks.some(
    (b) => !b.locked && b.text.trim().length > 0,
  );
  const hasAnyFavorite = blocks.some(
    (b) => b.favIdx >= 0 && !!b.takes[b.favIdx],
  );
  const genAllDisabled =
    pipelineMode === 'generate-all'
      ? false // cancel path always enabled
      : pipelineMode === 'play-all' ||
        generatingIdx !== null ||
        !hasUnlockedNonEmpty ||
        !!disabledReason;
  const playAllDisabled =
    pipelineMode === 'play-all'
      ? false
      : pipelineMode === 'generate-all' ||
        generatingIdx !== null ||
        !hasAnyFavorite;

  // Model lock + voice picker for the active block — identical for the
  // desktop ParamPanel and the mobile sheet, so computed once per render.
  const activeModelLock =
    activeBlock && activeAsset
      ? resolveModelLock(
          activeAsset,
          resolveBlockVoiceId(activeBlock, activeAsset.voiceIds ?? []),
          config?.voices ?? [],
        )
      : null;
  const activeVoiceSelection =
    activeBlock && activeAsset
      ? {
          voiceId: resolveBlockVoiceId(activeBlock, activeAsset.voiceIds ?? []),
          assetVoiceIds: activeAsset.voiceIds ?? [],
          projectVoices: config?.voices ?? [],
          onVoiceChange: handleBlockVoiceChange,
        }
      : undefined;

  return (
    <div className={styles.root}>
      {activeAsset?.script && !activeAsset?._isCustom && (
        <div className={styles.scriptBox}>
          <ScriptHeader
            label={<span>Original Script</span>}
            copyText={activeAsset?.script ?? ''}
            classes={scriptHeaderClasses}
            onRestore={handleRestoreScript}
          />
          <div className={styles.scriptBoxText}>{activeAsset.script}</div>
        </div>
      )}

      {/* Flex-Asset (Multi-Lingual ohne Video, Spec 2026-09-21 § 6): keine
          Video-Referenz. Hat das Asset ein Video, zeigt der Player es —
          ohne Upload-Zone, die kommt in Multi-Lingual nie. */}
      {!flexAsset && <VideoPlayer dockBandRef={timelineStickyRef} />}

      {/* Bed bar sits ABOVE the sticky timeline group so it scrolls away
          with the rest of the page content rather than pinning to the top.
          simpleMode: SFX/Music-Bed gehört nicht zum vereinfachten Flow. */}
      {!simpleMode && <SfxTrack />}

      <TtsTimelineBand
        stickyRef={timelineStickyRef}
        flexAsset={flexAsset}
        blocks={blocks}
        limitSec={activeAsset?.targetDuration}
        onBlocksReordered={handleBlocksReordered}
        onResetPositions={handleResetPositions}
      />

      <div className={styles.layout}>
        <div ref={blocksColRef} className={styles.blockCol}>
          <div className={styles.pipelineBar}>
            {/* Thai: kein Satz-Split möglich → ausgeblendet (Spec 2026-09-23-script-support-design) */}
            {detectScript(blocks.map((b) => b.text).join(' ')) !== 'thai' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={pipelineMode !== 'idle' || blocks.every((b) => !b.text.trim())}
                onClick={handleAutoSplitAll}
                data-coach="autosplit"
              >
                <i className="ph-bold ph-list-plus" /> Auto-Split into Blocks
              </Button>
            )}
            {autoSplitFeedback && (
              <span className={styles.pipelineFeedback}>{autoSplitFeedback}</span>
            )}
            <div className={styles.pipelineSpacer} />
            <Button
              variant={pipelineMode === 'generate-all' ? 'ghost' : 'primary'}
              size="sm"
              disabled={genAllDisabled}
              onClick={handleGenerateAll}
              data-coach="generate-all"
            >
              {pipelineMode === 'generate-all' ? (
                <>
                  <i className="ph-bold ph-x" /> Cancel
                </>
              ) : (
                <>
                  <i className="ph-fill ph-play" /> Generate All
                </>
              )}
            </Button>
          </div>
          {blocks.map((block, idx) => (
            <Block
              key={block.id}
              idx={idx}
              block={block}
              totalBlocks={blocks.length}
              isActive={idx === activeBlockIdx}
              isNew={idx === newBlockIdx}
              prevLocked={idx > 0 ? blocks[idx - 1].locked : false}
              nextLocked={
                idx < blocks.length - 1 ? blocks[idx + 1].locked : false
              }
              isGenerating={generatingIdx === idx || pipelineGenIdx === idx}
              isPipelineActive={pipelineBlockIdx === idx}
              playingTakeIdx={playing?.blockIdx === idx ? playing.takeIdx : -1}
              onActivate={handleActivate}
              onTextChange={handleTextChange}
              onToggleLock={handleToggleLock}
              onDuplicate={handleDuplicate}
              onMergeUp={handleMergeUp}
              onMergeDown={handleMergeDown}
              onSplit={handleSplit}
              onDelete={handleDelete}
              onDownload={handleDownloadBlock}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onChipPlay={handleChipPlay}
              onChipFav={handleChipFav}
              onChipDelete={handleChipDelete}
              onOpenParams={handleOpenParams}
              registerRef={registerRef}
              assetVoiceIds={activeAsset?.voiceIds}
              hintTakeId={hintTakeId}
              onHintDismiss={dismissStarHint}
            />
          ))}
          <div className={styles.addRow}>
            <button type="button" className={styles.addBtn} onClick={handleAddBlock}>
              + Add Block
            </button>
          </div>
        </div>

        {activeBlock && (
          <ParamPanel
            ref={paramPanelRef}
            activeBlockIdx={activeBlockIdx}
            params={activeBlock.params}
            onChange={handleParamsChange}
            modelLock={activeModelLock}
            activeBlockEl={activeBlockEl}
            blocksColEl={blocksColRef.current}
            skipTransitionToken={skipToken}
            onGenerate={() => handleGenerate(activeBlockIdx)}
            canGenerate={!activeBlock.locked && !!activeBlock.text.trim()}
            isGenerating={generatingIdx === activeBlockIdx}
            disabledReason={disabledReason}
            voiceSelection={activeVoiceSelection}
          />
        )}
      </div>

      <ScrollHint
        targetEl={blocksColRef.current}
        label="Scroll down to start generating..."
      />

      {coach.active && (
        <CoachMark
          key={coach.active}
          targets={[...COACH_TARGETS[coach.active]]}
          text={COACH_MARK_TEXTS[coach.active]}
          placement={coach.active === 'timeline' ? 'below' : 'above'}
          arrows={coach.active === 'generate'}
          // Befestigung automatisch: mitscrollend, solange das Ziel scrollt;
          // fixed, sobald Band bzw. BottomBar haften. Mitscrollende Blasen
          // weichen dem sticky Band aus (klappen unter ihr Ziel).
          avoid="timeline"
          // Auto-Split, Generate und Upload: der Klick auf den Button genügt
          // (nicht erst der fertige Take). Timeline-Hinweis bleibt, bis
          // gezogen oder × geklickt wird.
          dismissOnTargetClick={coach.active !== 'timeline'}
          onDismiss={coach.dismiss}
        />
      )}

      <BottomBar
        onPlayAll={handlePlayAllFavorites}
        playAllDisabled={playAllDisabled}
        playAllActive={pipelineMode === 'play-all'}
        onUpload={() => setUploadOpen(true)}
        uploadDisabled={!allBlocksReady || !sessionFolder}
        uploadDisabledReason={uploadDisabledReason}
        currentAssetIdx={currentAssetIdx}
        totalAssets={totalAssets}
        onPrev={() => {
          if (currentAssetIdx > 0) setCurrentAssetIdx(currentAssetIdx - 1);
        }}
        onNext={() => {
          if (currentAssetIdx < totalAssets - 1) {
            setCurrentAssetIdx(currentAssetIdx + 1);
          }
        }}
        onSummary={() => {
          if (routeId) navigate(withSearch(`/${routeId}/summary`, location.search));
        }}
      />

      {activeBlock && (
        <MobileParamSheet
          open={mobilePanelOpen}
          onClose={closeMobilePanel}
          activeBlockIdx={activeBlockIdx}
          params={activeBlock.params}
          onChange={handleParamsChange}
          modelLock={activeModelLock}
          onGenerate={() => {
            handleGenerate(activeBlockIdx);
            closeMobilePanel();
          }}
          canGenerate={!activeBlock.locked && !!activeBlock.text.trim()}
          isGenerating={generatingIdx === activeBlockIdx}
          disabledReason={disabledReason}
          voiceSelection={activeVoiceSelection}
        />
      )}

      {activeAsset && (
        <UploadModal
          open={uploadOpen}
          nextVersion={nextVersion}
          readyCount={readyBlocks.length}
          hasVideo={hasVideoForActive}
          cloudVideoSize={cloudVideoSize}
          onClose={() => setUploadOpen(false)}
          onSubmit={handleUploadSubmit}
        />
      )}
    </div>
  );
}
